import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../UI/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faShoppingBag, faTimes, faStar as fasStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';

const CardWrapper = styled(motion.div)`
  overflow: hidden;
  position: relative;
  width: 100%;
  max-width: 320px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 12px;
  transition: all 0.3s ease-in-out;
  background: linear-gradient(135deg, #ffffff, #f8f9fa);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  margin: 0 auto;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1);
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  }
  
  @media (max-width: 576px) {
    max-width: 100%;
    width: 100%;
  }
`;

const SelectOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(33, 150, 243, 0.1);
  z-index: 10;
  pointer-events: none;
  border-radius: 12px;
  border: 2px solid rgba(33, 150, 243, 0);
  transition: border 0.3s ease;

  ${CardWrapper}:hover & {
    border: 2px solid rgba(33, 150, 243, 0.6);
  }
`;

const SelectCheckmark = styled(motion.div)`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2196f3, #1976d2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.5);
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: var(--border-radius-md);
  margin-bottom: var(--spacing-md);
  cursor: pointer;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.05) 100%);
    pointer-events: none;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
  
  ${ImageContainer}:hover & {
    transform: scale(1.05);
  }
`;

const BadgeWrapper = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #ff6b6b, #f03e3e);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 8px rgba(240, 62, 62, 0.3);
  z-index: 2;
  transition: all 0.3s ease;
`;

const TagWrapper = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, #ff6b6b, #f03e3e);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 8px rgba(240, 62, 62, 0.3);
  z-index: 2;
  transition: all 0.3s ease;
`;

const StockIndicator = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #4caf50;
  margin-bottom: 12px;
  
  svg {
    margin-right: 6px;
  }
`;

const DiscountWrapper = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #ff6b6b, #f03e3e);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 8px rgba(240, 62, 62, 0.3);
  z-index: 2;
  transition: all 0.3s ease;
`;

const PlayButton = styled.button`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ff5722;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(255, 87, 34, 0.3);
  font-size: 18px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    background: #e64a19;
  }
`;

const CardContent = styled.div`
  padding: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Title = styled.h3`
  font-size: 18px;
  margin-bottom: 8px;
  color: var(--color-text-dark);
  font-weight: 600;
  transition: color 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  ${CardWrapper}:hover & {
    color: #1976d2;
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: var(--color-text);
  margin-bottom: 12px;
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const OldPrice = styled.span`
  font-size: 14px;
  text-decoration: line-through;
  color: #999;
  margin-bottom: 4px;
`;

const MainPrice = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #2196f3;
  transition: transform 0.3s ease;
  
  ${CardWrapper}:hover & {
    transform: scale(1.1);
  }
`;

const WholesalePrice = styled.span`
  font-size: 14px;
  color: #555;
  margin-top: 4px;
`;

const ReviewInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  
  svg {
    color: #ffc107;
    margin-right: 4px;
    font-size: 14px;
  }
`;

const ReviewCount = styled.span`
  font-size: 14px;
  color: #757575;
  margin-left: 6px;
`;

const ButtonWrapper = styled.div`
  margin-top: auto;
  transition: transform 0.3s ease;
  
  ${CardWrapper}:hover & {
    transform: translateY(-5px);
  }
`;

// Стили для модального окна заказа
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: color 0.3s;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }
`;

const InputNumber = styled(Input)`
  width: 100px;
`;

const OrderSummary = styled.div`
  margin: 20px 0;
  padding: 15px;
  border-radius: 8px;
  background-color: #f5f9ff;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  
  &:last-child {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
    font-weight: 700;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 20px;
  padding: 12px 0;
  font-size: 16px;
  font-weight: 600;
`;

const OrderSuccess = styled.div`
  text-align: center;
  padding: 20px;
  
  h4 {
    color: #43a047;
    margin-bottom: 16px;
    font-size: 22px;
  }
  
  p {
    margin-bottom: 24px;
    color: #666;
  }
`;

const ProductCard = ({ product, onOrder, delay = 0, ...props }) => {
  const { 
    id, 
    name, 
    description, 
    price, 
    imageUrl, 
    oldPrice, 
    discount, 
    wholesalePrice, 
    inStock, 
    rating,
    reviews,
    tag
  } = product;
  const [isSelected, setIsSelected] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  
  // Извлекаем числовое значение цены из строки (например, из "от 150 руб/рулон" получаем 150)
  const extractPrice = (priceString) => {
    const match = priceString.match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : 0;
  };
  
  const basePrice = extractPrice(price);
  const totalPrice = basePrice * quantity;
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderDetails(prev => ({ ...prev, [name]: value }));
  };
  
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setQuantity(value > 0 ? value : 1);
  };
  
  const handleOrderClick = () => {
    setShowOrderModal(true);
  };
  
  const handleCloseModal = () => {
    setShowOrderModal(false);
    // Если заказ был успешным, сбрасываем состояние при закрытии
    if (orderSuccess) {
      setOrderSuccess(false);
      setQuantity(1);
      setOrderDetails({
        name: '',
        phone: '',
        email: '',
        address: ''
      });
    }
  };
  
  const handleOrderSubmit = (e) => {
    e.preventDefault();
    // Здесь будет логика отправки заказа
    onOrder({
      productId: id,
      name,
      quantity,
      totalPrice,
      customerInfo: orderDetails
    });
    setShowOrderModal(false);
    setQuantity(1);
    setOrderDetails({
      name: '',
      phone: '',
      email: '',
      address: ''
    });
  };
  
  // Create star rating display
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars.push(<FontAwesomeIcon key={i} icon={fasStar} />);
      } else {
        stars.push(<FontAwesomeIcon key={i} icon={farStar} />);
      }
    }
    return stars;
  };
  
  return (
    <>
      <CardWrapper
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay }}
        {...props}
      >
        <SelectOverlay
          animate={{ opacity: isSelected ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
        
        {isSelected && (
          <SelectCheckmark
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          >
            <FontAwesomeIcon icon={faCheck} />
          </SelectCheckmark>
        )}
        
        <ImageContainer onClick={handleOrderClick}>
          {discount && <DiscountWrapper>{discount}</DiscountWrapper>}
          {tag && <TagWrapper>{tag}</TagWrapper>}
          <PlayButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation();
              setShowOrderModal(true);
            }}
          >
            <FontAwesomeIcon icon={faShoppingBag} />
          </PlayButton>
          <ProductImage src={imageUrl} alt={name} />
        </ImageContainer>
        <CardContent>
          <Title>{name}</Title>
          <Description>{description}</Description>
          
          <ReviewInfo>
            {renderStars(rating || 5)}
            <ReviewCount>{reviews || 0} отзыв{reviews !== 1 ? 'ов' : ''}</ReviewCount>
          </ReviewInfo>
          
          {inStock && (
            <StockIndicator>
              <FontAwesomeIcon icon={faCheck} />
              В наличии на складе
            </StockIndicator>
          )}
          
          <PriceBlock>
            {oldPrice && <OldPrice>{oldPrice}</OldPrice>}
            <MainPrice>{price}</MainPrice>
            {wholesalePrice && <WholesalePrice>{wholesalePrice}</WholesalePrice>}
          </PriceBlock>
          
          <ButtonWrapper>
            <Button 
              variant={'contained'} 
              fullWidth 
              onClick={handleOrderClick}
            >
              Заказать
            </Button>
          </ButtonWrapper>
        </CardContent>
      </CardWrapper>
      
      <AnimatePresence>
        {showOrderModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              {orderSuccess ? (
                <OrderSuccess>
                  <h4>Заказ успешно отправлен!</h4>
                  <p>Мы свяжемся с вами в ближайшее время для подтверждения заказа.</p>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleCloseModal}
                  >
                    Закрыть
                  </Button>
                </OrderSuccess>
              ) : (
                <>
                  <ModalHeader>
                    <ModalTitle>Оформление заказа</ModalTitle>
                    <CloseButton onClick={handleCloseModal}>
                      <FontAwesomeIcon icon={faTimes} />
                    </CloseButton>
                  </ModalHeader>
                  
                  <form onSubmit={handleOrderSubmit}>
                    <div>
                      <strong>Товар:</strong> {name}
                    </div>
                    
                    <FormGroup>
                      <Label htmlFor="quantity">Количество (шт)</Label>
                      <InputNumber 
                        type="number" 
                        id="quantity" 
                        min="1" 
                        value={quantity} 
                        onChange={handleQuantityChange} 
                        required 
                      />
                    </FormGroup>
                    
                    <OrderSummary>
                      <SummaryRow>
                        <span>Стоимость за ед.:</span>
                        <span>{basePrice.toFixed(2)} руб.</span>
                      </SummaryRow>
                      <SummaryRow>
                        <span>Количество:</span>
                        <span>{quantity} шт.</span>
                      </SummaryRow>
                      <SummaryRow>
                        <span>Итого к оплате:</span>
                        <span>{totalPrice.toFixed(2)} руб.</span>
                      </SummaryRow>
                    </OrderSummary>
                    
                    <FormGroup>
                      <Label htmlFor="name">Ваше имя</Label>
                      <Input 
                        type="text" 
                        id="name" 
                        name="name" 
                        value={orderDetails.name} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label htmlFor="phone">Телефон</Label>
                      <Input 
                        type="tel" 
                        id="phone" 
                        name="phone" 
                        value={orderDetails.phone} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        type="email" 
                        id="email" 
                        name="email" 
                        value={orderDetails.email} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label htmlFor="address">Адрес доставки</Label>
                      <Input 
                        type="text" 
                        id="address" 
                        name="address" 
                        value={orderDetails.address} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </FormGroup>
                    
                    <SubmitButton 
                      type="submit" 
                      variant="contained" 
                      color="primary"
                    >
                      Отправить заявку
                    </SubmitButton>
                  </form>
                </>
              )}
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductCard; 